import React, { useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import moment from 'moment';
import styled from 'styled-components';
import { useApi } from '~/contexts';
import { colors } from '~/styles';
import { ChartLabel } from './shared';

const Wrapper = styled.div`
  height: 20rem;
`;

export default function CostOverTime({ start, end, granularity }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!start || !end) return;
    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      setData(null);
      try {
        const { data } = await api.admin.aiDashboard.getOverTime({ start, end, granularity });
        if (!cancelled) setData(data);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [api, start, end, granularity]);

  const chartData = useMemo(() => {
    if (!data) return null;

    const formatMap = { day: 'MMM D', week: 'MMM D', month: 'MMM YYYY' };
    const format = formatMap[granularity] || 'MMM D';

    return {
      labels: data.map((d) => moment.utc(d.date).format(format)),
      datasets: [
        {
          label: 'Input Cost',
          data: data.map((d) => d.inputCost),
          backgroundColor: colors.primary,
        },
        {
          label: 'Output Cost',
          data: data.map((d) => d.outputCost),
          backgroundColor: colors.accent,
        },
      ],
    };
  }, [data, granularity]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: { stacked: true },
      y: {
        stacked: true,
        ticks: { callback: (value) => `$${value.toFixed(2)}` },
      },
    },
    interaction: { mode: 'index' },
    plugins: {
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label}: $${context.raw.toFixed(2)}`,
          footer: (items) => {
            const total = items.reduce((sum, item) => sum + item.raw, 0);
            return `Total: $${total.toFixed(2)}`;
          },
        },
      },
    },
  };

  if (isLoading || !data) return null;

  const labelMap = { day: 'Daily', week: 'Weekly', month: 'Monthly' };

  return (
    <div>
      <ChartLabel>Cost ({labelMap[granularity] || 'Daily'})</ChartLabel>
      <Wrapper>
        <Bar data={chartData} options={options} />
      </Wrapper>
    </div>
  );
}
